import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDialog } from '../context/DialogContext';
import { useLocale } from '../context/LocaleContext';
import listStyles from '../styles/step8ListModal.module.css';
import styles from '../styles/threeCirclesModal.module.css';

export interface ThreeCirclesData {
  inner: string[];
  middle: string[];
  outer: string[];
}

export const EMPTY_THREE_CIRCLES: ThreeCirclesData = { inner: [], middle: [], outer: [] };

interface ThreeCirclesModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  circles: ThreeCirclesData;
  onSave: (circles: ThreeCirclesData) => Promise<void>;
}

type CircleKey = keyof ThreeCirclesData;

const CIRCLE_KEYS: CircleKey[] = ['inner', 'middle', 'outer'];

const ThreeCirclesModal: React.FC<ThreeCirclesModalProps> = ({
  isOpen,
  onClose,
  user,
  circles,
  onSave,
}) => {
  const { showConfirm } = useDialog();
  const { t, locale } = useLocale();
  const [localCircles, setLocalCircles] = useState<ThreeCirclesData>(circles);
  const [inputs, setInputs] = useState<Record<CircleKey, string>>({ inner: '', middle: '', outer: '' });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalCircles(circles);
      setInputs({ inner: '', middle: '', outer: '' });
      setHasUnsavedChanges(false);
    }
  }, [isOpen, circles]);

  const scheduleSave = useCallback(
    (nextCircles: ThreeCirclesData) => {
      setHasUnsavedChanges(true);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(async () => {
        if (!user) return;
        setSaving(true);
        try {
          await onSave(nextCircles);
          setSavedAt(new Date());
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error('Error saving 3 Circles:', error);
        } finally {
          setSaving(false);
        }
      }, 400);
    },
    [onSave, user]
  );

  const handleAdd = (key: CircleKey, e: React.FormEvent) => {
    e.preventDefault();
    const value = inputs[key].trim();
    if (!value) return;
    const nextCircles = {
      ...localCircles,
      [key]: [...localCircles[key], value],
    };
    setLocalCircles(nextCircles);
    setInputs((prev) => ({ ...prev, [key]: '' }));
    scheduleSave(nextCircles);
  };

  const handleDelete = (key: CircleKey, index: number) => {
    const item = localCircles[key][index];
    showConfirm({
      message: t('getting_started.circles.remove_confirm', {
        item,
        circle: t(`getting_started.circles.${key}.name`),
      }),
      onConfirm: () => {
        const nextCircles = {
          ...localCircles,
          [key]: localCircles[key].filter((_, i) => i !== index),
        };
        setLocalCircles(nextCircles);
        scheduleSave(nextCircles);
      },
      variant: 'danger',
    });
  };

  const handleClose = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    if (hasUnsavedChanges && user) {
      setSaving(true);
      try {
        await onSave(localCircles);
      } catch (error) {
        console.error('Error saving 3 Circles on close:', error);
      } finally {
        setSaving(false);
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  const formatSavedTime = () => {
    if (!savedAt) return null;
    return savedAt.toLocaleTimeString(locale || [], { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className={listStyles.overlay} onClick={handleClose}>
      <div className={listStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={listStyles.header}>
          <h2 className={listStyles.title}>{t('getting_started.circles.modal_title')}</h2>
          <button
            className={listStyles.closeButton}
            onClick={handleClose}
            aria-label={t('getting_started.circles.close')}
          >
            ×
          </button>
        </div>

        <div className={listStyles.content}>
          {!user ? (
            <div className={listStyles.loginPrompt}>
              <p>{t('getting_started.circles.login_prompt')}</p>
            </div>
          ) : (
            <>
              <div className={listStyles.description}>
                <p>{t('getting_started.circles.intro')}</p>
              </div>

              {CIRCLE_KEYS.map((key) => (
                <div key={key} className={styles.circleSection}>
                  <h3 className={styles.circleHeading}>
                    {t(`getting_started.circles.${key}.title`)}{' '}
                    <span className={styles.circleSubheading}>
                      — {t(`getting_started.circles.${key}.subtitle`)}
                    </span>
                  </h3>
                  <form className={listStyles.quickForm} onSubmit={(e) => handleAdd(key, e)}>
                    <div className={listStyles.formGroup}>
                      <div className={listStyles.quickInputGroup}>
                        <input
                          type="text"
                          value={inputs[key]}
                          onChange={(e) => setInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                          placeholder={t(`getting_started.circles.${key}.placeholder`)}
                          className={listStyles.quickInput}
                        />
                        <button type="submit" className={listStyles.btnSuccess}>
                          {t('getting_started.circles.add_to_list')}
                        </button>
                      </div>
                    </div>
                  </form>
                  {localCircles[key].length > 0 && (
                    <ul className={styles.simpleList}>
                      {localCircles[key].map((item, idx) => (
                        <li key={`${key}-${idx}`} className={styles.simpleListItem}>
                          <span className={styles.simpleListItemText}>{item}</span>
                          <button
                            type="button"
                            onClick={() => handleDelete(key, idx)}
                            className={listStyles.deleteButton}
                            title={t('getting_started.circles.remove')}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {user && (
          <div className={styles.footer}>
            <span className={styles.saveStatus}>
              {saving
                ? t('getting_started.circles.saving')
                : hasUnsavedChanges
                  ? t('getting_started.circles.unsaved')
                  : t('getting_started.circles.saved_encrypted')}
              {!saving &&
                !hasUnsavedChanges &&
                savedAt &&
                t('getting_started.circles.last_saved', { time: formatSavedTime() ?? '' })}
            </span>
            <button type="button" className={styles.closeFooterButton} onClick={handleClose}>
              {t('getting_started.circles.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreeCirclesModal;
